/**
 * Language utilities for displaying language information with flags
 */

// Map of language codes to flag emojis and names
export const LANGUAGE_FLAGS: Record<string, { flag: string; name: string }> = {
  // Major languages
  en: { flag: "🇺🇸", name: "English" },
  es: { flag: "🇪🇸", name: "Spanish" },
  fr: { flag: "🇫🇷", name: "French" },
  de: { flag: "🇩🇪", name: "German" },
  zh: { flag: "🇨🇳", name: "Chinese" },
  hi: { flag: "🇮🇳", name: "Hindi" },
  ar: { flag: "🇸🇦", name: "Arabic" },

  // European languages
  it: { flag: "🇮🇹", name: "Italian" },
  pt: { flag: "🇵🇹", name: "Portuguese" },
  ru: { flag: "🇷🇺", name: "Russian" },
  nl: { flag: "🇳🇱", name: "Dutch" },
  sv: { flag: "🇸🇪", name: "Swedish" },
  pl: { flag: "🇵🇱", name: "Polish" },
  no: { flag: "🇳🇴", name: "Norwegian" },
  da: { flag: "🇩🇰", name: "Danish" },
  fi: { flag: "🇫🇮", name: "Finnish" },
  cs: { flag: "🇨🇿", name: "Czech" },
  hu: { flag: "🇭🇺", name: "Hungarian" },
  ro: { flag: "🇷🇴", name: "Romanian" },
  el: { flag: "🇬🇷", name: "Greek" },
  uk: { flag: "🇺🇦", name: "Ukrainian" },

  // Asian languages
  ja: { flag: "🇯🇵", name: "Japanese" },
  ko: { flag: "🇰🇷", name: "Korean" },
  vi: { flag: "🇻🇳", name: "Vietnamese" },
  th: { flag: "🇹🇭", name: "Thai" },
  id: { flag: "🇮🇩", name: "Indonesian" },
  ms: { flag: "🇲🇾", name: "Malay" },
  ta: { flag: "🇮🇳", name: "Tamil" },
  te: { flag: "🇮🇳", name: "Telugu" },
  bn: { flag: "🇧🇩", name: "Bengali" },
  ur: { flag: "🇵🇰", name: "Urdu" },

  // Middle Eastern
  tr: { flag: "🇹🇷", name: "Turkish" },
  fa: { flag: "🇮🇷", name: "Persian" },
  he: { flag: "🇮🇱", name: "Hebrew" },

  // Others
  sw: { flag: "🇰🇪", name: "Swahili" },
  fil: { flag: "🇵🇭", name: "Filipino" },
  tl: { flag: "🇵🇭", name: "Tagalog" },

  // Default
  default: { flag: "🌐", name: "Other" }
};

/**
 * Get flag emoji for a language code
 */
export function getLanguageFlag(languageCode: string): string {
  const code = languageCode.toLowerCase().split('-')[0]; // Handle codes like en-US
  return LANGUAGE_FLAGS[code]?.flag || LANGUAGE_FLAGS.default.flag;
}

/**
 * Get language name for a language code
 */
export function getLanguageName(languageCode: string): string {
  const code = languageCode.toLowerCase().split('-')[0];
  return LANGUAGE_FLAGS[code]?.name || languageCode;
}

/**
 * Get language info (flag + name) for a language code
 */
export function getLanguageInfo(languageCode: string): { flag: string; name: string } {
  const code = languageCode.toLowerCase().split('-')[0];
  return LANGUAGE_FLAGS[code] || { flag: LANGUAGE_FLAGS.default.flag, name: languageCode };
}

/**
 * Format language with flag for display
 */
export function formatLanguageWithFlag(languageCode: string): string {
  const info = getLanguageInfo(languageCode);
  return `${info.flag} ${info.name}`;
}

/**
 * Get top N languages from a list of language codes
 */
export function getTopLanguages(languages: string[], limit: number = 3): string[] {
  // Prioritize major languages
  const priorityOrder = ['en', 'es', 'fr', 'de', 'zh', 'hi', 'ar', 'pt', 'ru', 'ja'];

  const sorted = [...languages].sort((a, b) => {
    const aCode = a.toLowerCase().split('-')[0];
    const bCode = b.toLowerCase().split('-')[0];
    const aPriority = priorityOrder.indexOf(aCode);
    const bPriority = priorityOrder.indexOf(bCode);

    if (aPriority !== -1 && bPriority !== -1) {
      return aPriority - bPriority;
    }
    if (aPriority !== -1) return -1;
    if (bPriority !== -1) return 1;
    return 0;
  });

  return sorted.slice(0, limit);
}

/**
 * Get language from accent string
 * Maps common accent strings to their primary language
 */
export function getLanguageFromAccent(accent: string): string {
  const accentMap: Record<string, string> = {
    'american': 'en',
    'british': 'en',
    'australian': 'en',
    'irish': 'en',
    'scottish': 'en',
    'canadian': 'en',
    'indian': 'en',
    'south african': 'en',
    'new zealand': 'en',

    'spanish': 'es',
    'mexican': 'es',
    'argentinian': 'es',

    'french': 'fr',
    'german': 'de',
    'italian': 'it',
    'portuguese': 'pt',
    'brazilian': 'pt',
    'russian': 'ru',
    'chinese': 'zh',
    'mandarin': 'zh',
    'japanese': 'ja',
    'korean': 'ko',
    'arabic': 'ar',
    'dutch': 'nl',
    'swedish': 'sv',
    'norwegian': 'no',
    'danish': 'da',
    'finnish': 'fi',
    'polish': 'pl',
    'turkish': 'tr',
    'greek': 'el',
    'hebrew': 'he',
    'hindi': 'hi',
    'thai': 'th',
    'vietnamese': 'vi',
    'indonesian': 'id',
  };

  const lowerAccent = accent.toLowerCase();
  for (const [key, lang] of Object.entries(accentMap)) {
    if (lowerAccent.includes(key)) {
      return lang;
    }
  }

  // Default to English if accent not recognized
  return 'en';
}